// © 2025 Camp Promethean, LLC. All Rights Reserved.
// Utility functions for managing selections

import { 
  EnhancedSelection, 
  Selection, 
  SelectionTag, 
  SelectionBadge, 
  BADGE_TYPES 
} from '@/types/selection';
import { PhenoSensory, SensoryData } from '@/types/sensory';
import { PlantTraitFormData } from '@/components/PlantTraitDialog';

// Predefined tag categories with colors
export const TAG_CATEGORIES = {
  growth: {
    name: 'Growth',
    color: '#10b981' // Green
  },
  sensory: {
    name: 'Sensory',
    color: '#8b5cf6' // Purple
  },
  breeding: {
    name: 'Breeding',
    color: '#3b82f6' // Blue
  },
  custom: {
    name: 'Custom',
    color: '#f59e0b' // Amber
  },
  // Add additional categories for compatibility
  aroma: {
    name: 'Aroma',
    color: '#8b5cf6' // Purple
  },
  flavor: {
    name: 'Flavor',
    color: '#ec4899' // Pink
  },
  appearance: {
    name: 'Appearance',
    color: '#3b82f6' // Blue
  },
  effects: {
    name: 'Effects',
    color: '#f59e0b' // Amber
  },
  terpenes: {
    name: 'Terpenes',
    color: '#8b5cf6' // Purple
  }
};

// Predefined tags for each category
export const PREDEFINED_TAGS = {
  growth: [
    'High Yield',
    'Fast Growth',
    'Strong Structure',
    'Dense Buds',
    'Heavy Frost',
    'Mold Resistant',
    'Pest Resistant',
    'Drought Tolerant',
    'Heat Tolerant',
    'Cold Tolerant'
  ],
  sensory: [
    'Citrus',
    'Earthy',
    'Fruity',
    'Gassy',
    'Pine',
    'Skunk',
    'Sweet',
    'Spicy',
    'Smooth Smoke',
    'Clean Ash',
    'Long Lasting',
    'Potent'
  ],
  breeding: [
    'F1 Parent',
    'Pollen Donor',
    'Mother Plant',
    'Stable Traits',
    'Hybrid Vigor',
    'Autoflower',
    'Photoperiod',
    'Feminized',
    'Regular'
  ],
  custom: [
    'Favorite',
    'Experimental',
    'Unique',
    'Rare',
    'Commercial',
    'Medical',
    'Recreational'
  ]
};

/**
 * Creates a selection tag with a unique ID
 * Overloaded function to accept any string for category
 */
export function createTag(name: string, category: string): SelectionTag {
  // Only include the properties that match the SelectionTag interface
  return {
    id: `tag-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    name,
    category
  };
}

/**
 * Generates badges for a selection based on its traits and sensory data
 */
export function generateBadges(
  plantData: PlantTraitFormData,
  sensoryData?: PhenoSensory | null
): SelectionBadge[];

/**
 * Overloaded function to generate badges for the new EnhancedSelection format
 */
export function generateBadges(selection: EnhancedSelection): SelectionBadge[];

// Implementation of the generateBadges function that handles both signatures
export function generateBadges(
  dataOrSelection: PlantTraitFormData | EnhancedSelection,
  sensoryData?: PhenoSensory | null
): SelectionBadge[] {
  const badges: SelectionBadge[] = [];

  // Check if we're dealing with the new EnhancedSelection format
  if ('growthMetrics' in dataOrSelection) {
    const selection = dataOrSelection as EnhancedSelection;
    
    // Check for top producer badge
    if (selection.growthMetrics.yield >= 8) {
      badges.push({
        id: BADGE_TYPES.TOP_PRODUCER,
        name: 'Top Producer',
        description: 'Exceptional yield potential',
        color: '#10b981' // Green
      });
    }
    
    // Check for frost monster badge
    if (selection.growthMetrics.frost >= 8) {
      badges.push({
        id: BADGE_TYPES.FROST_MONSTER,
        name: 'Frost Monster',
        description: 'Heavy trichome production',
        color: '#60a5fa' // Light blue
      });
    }
    
    // Check for structure champion badge
    if (selection.growthMetrics.structure >= 8) {
      badges.push({
        id: BADGE_TYPES.STRUCTURE_CHAMPION,
        name: 'Structure Champion',
        description: 'Excellent plant architecture',
        color: '#8b5cf6' // Purple
      });
    }
    
    // Check for sensory badges if sensory data is available
    if (selection.sensoryData) {
      // Check for aroma star badge
      if (selection.sensoryData.aroma_quality && selection.sensoryData.aroma_quality >= 8) {
        badges.push({
          id: BADGE_TYPES.AROMA_STAR,
          name: 'Aroma Star',
          description: 'Outstanding terpene profile',
          color: '#ec4899' // Pink
        });
      }
      
      // Check for smooth smoke badge
      if (selection.sensoryData.smoke_harshness && selection.sensoryData.smoke_harshness <= 3) {
        badges.push({
          id: BADGE_TYPES.SMOOTH_SMOKE,
          name: 'Smooth Smoke',
          description: 'Exceptionally smooth smoking experience',
          color: '#6366f1' // Indigo
        });
      }
      
      // Check for flavor profile badge
      if (selection.sensoryData.flavor_quality && selection.sensoryData.flavor_quality >= 8) {
        badges.push({
          id: BADGE_TYPES.FLAVOR_PROFILE,
          name: 'Flavor Profile',
          description: 'Exceptional taste and flavor',
          color: '#f59e0b' // Amber
        });
      }
    }
    
    // Check for breeding potential badge
    if (selection.growthMetrics.overall >= 8) {
      badges.push({
        id: BADGE_TYPES.BREEDING_POTENTIAL,
        name: 'Breeding Potential',
        description: 'Excellent candidate for breeding program',
        color: '#0ea5e9' // Sky blue
      });
    }
  } 
  // Original implementation for the PlantTraitFormData format
  else {
    const plantData = dataOrSelection as PlantTraitFormData;
    
    // Check for top producer badge
    if (plantData.traits.yield >= 8) {
      badges.push({
        id: BADGE_TYPES.TOP_PRODUCER,
        name: 'Top Producer',
        description: 'Exceptional yield potential',
        color: '#10b981' // Green
      });
    }
    
    // Check for frost monster badge
    if (plantData.traits.frost >= 8) {
      badges.push({
        id: BADGE_TYPES.FROST_MONSTER,
        name: 'Frost Monster',
        description: 'Heavy trichome production',
        color: '#60a5fa' // Light blue
      });
    }
    
    // Check for structure champion badge
    if (plantData.traits.structure >= 8) {
      badges.push({
        id: BADGE_TYPES.STRUCTURE_CHAMPION,
        name: 'Structure Champion',
        description: 'Excellent plant architecture',
        color: '#8b5cf6' // Purple
      });
    }
    
    // Check for aroma star badge
    if (plantData.traits.aroma >= 8) {
      badges.push({
        id: BADGE_TYPES.AROMA_STAR,
        name: 'Aroma Star',
        description: 'Outstanding terpene profile',
        color: '#ec4899' // Pink
      });
    }
    
    // Check for sensory badges if sensory data is available
    if (sensoryData) {
      // Check for smooth smoke badge
      if (sensoryData.smoke_harshness && sensoryData.smoke_harshness >= 8) {
        badges.push({
          id: BADGE_TYPES.SMOOTH_SMOKE,
          name: 'Smooth Smoke',
          description: 'Exceptionally smooth smoking experience',
          color: '#6366f1' // Indigo
        });
      }
      
      // Check for flavor profile badge
      if (sensoryData.flavor_quality && sensoryData.flavor_quality >= 8) {
        badges.push({
          id: BADGE_TYPES.FLAVOR_PROFILE,
          name: 'Flavor Profile',
          description: 'Exceptional taste and flavor',
          color: '#f59e0b' // Amber
        });
      }
    }
    
    // Check for breeding potential badge
    if (plantData.traits.overall >= 8) {
      badges.push({
        id: BADGE_TYPES.BREEDING_POTENTIAL,
        name: 'Breeding Potential',
        description: 'Excellent candidate for breeding program',
        color: '#0ea5e9' // Sky blue
      });
    }
  }
  
  // Don't sort by priority since we don't have that property anymore
  return badges;
}

/**
 * Generates selection highlights based on traits and sensory data
 */
export function generateHighlights(
  plantData: PlantTraitFormData,
  sensoryData?: PhenoSensory | null
): string[] {
  const highlights: string[] = [];
  
  // Add highlights based on growth traits
  if (plantData.traits.vigor >= 8) highlights.push('Exceptional vigor');
  if (plantData.traits.structure >= 8) highlights.push('Outstanding structure');
  if (plantData.traits.yield >= 8) highlights.push('High yield potential');
  if (plantData.traits.frost >= 8) highlights.push('Heavy trichome coverage');
  
  // Add highlights based on sensory data if available
  if (sensoryData) {
    if (sensoryData.aroma_quality && sensoryData.aroma_quality >= 8) {
      highlights.push('Exceptional aroma quality');
    }
    
    if (sensoryData.flavor_quality && sensoryData.flavor_quality >= 8) {
      highlights.push('Outstanding flavor profile');
    }
    
    if (sensoryData.smoke_harshness && sensoryData.smoke_harshness >= 8) {
      highlights.push('Exceptionally smooth smoke');
    }
    
    // Add aroma profile highlights
    if (sensoryData.aroma_profile && sensoryData.aroma_profile.length > 0) {
      highlights.push(`Notable aromas: ${sensoryData.aroma_profile.join(', ')}`);
    }
  }
  
  return highlights;
}

/**
 * Transforms a database selection and plant data into an enhanced selection
 */
export function transformToEnhancedSelection(
  selection: Selection,
  plantData: PlantTraitFormData,
  sensoryData?: PhenoSensory | null
): EnhancedSelection;

/**
 * Overloaded function to transform database selection with SensoryData
 */
export function transformToEnhancedSelection(
  selection: Selection,
  sensoryData?: SensoryData | null,
  tags?: SelectionTag[],
  badges?: SelectionBadge[]
): EnhancedSelection;

/**
 * Implementation of transformToEnhancedSelection that handles both signatures
 */
export function transformToEnhancedSelection(
  selection: Selection,
  dataOrSensory?: PlantTraitFormData | SensoryData | PhenoSensory | null,
  tagsOrSensory?: SelectionTag[] | PhenoSensory | null,
  badges?: SelectionBadge[]
): EnhancedSelection {
  // Check which overload we're dealing with
  if (dataOrSensory && 'traits' in dataOrSensory) {
    // Original implementation for the PlantTraitFormData overload
    const plantData = dataOrSensory as PlantTraitFormData;
    const sensoryData = tagsOrSensory as PhenoSensory | null;
    
    // Generate tags based on traits and sensory data
    const tags: SelectionTag[] = [];
    
    // Add growth tags
    if (plantData.traits.vigor >= 7) tags.push(createTag('High Vigor', 'growth'));
    if (plantData.traits.structure >= 7) tags.push(createTag('Strong Structure', 'growth'));
    if (plantData.traits.yield >= 7) tags.push(createTag('High Yield', 'growth'));
    if (plantData.traits.frost >= 7) tags.push(createTag('Heavy Frost', 'growth'));
    
    // Add sensory tags if sensory data is available
    if (sensoryData) {
      if (sensoryData.aroma_profile) {
        sensoryData.aroma_profile.forEach(profile => {
          tags.push(createTag(profile, 'sensory'));
        });
      }
      
      if (sensoryData.flavor_profile) {
        sensoryData.flavor_profile.forEach(profile => {
          tags.push(createTag(profile, 'sensory'));
        });
      }
    }
    
    // Generate badges
    const selectionBadges = generateBadges(plantData, sensoryData);
    
    // Generate highlights
    const highlights = generateHighlights(plantData, sensoryData);
    
    // Construct the enhanced selection
    return {
      id: selection.id,
      phenoId: selection.pheno_id,
      plantId: selection.plant_id,
      generation: selection.generation,
      selectionDate: selection.selection_date,
      selectionRationale: selection.selection_rationale,
      selectionNotes: selection.selection_notes,
      selectionHighlights: highlights,
      growthMetrics: {
        structure: plantData.traits.structure,
        vigor: plantData.traits.vigor,
        yield: plantData.traits.yield,
        frost: plantData.traits.frost,
        overall: plantData.traits.overall
      },
      sensoryData: sensoryData ? {
        id: sensoryData.id,
        pheno_id: sensoryData.pheno_id,
        plant_id: sensoryData.plant_id,
        aroma_intensity: sensoryData.aroma_intensity,
        aroma_quality: sensoryData.aroma_quality,
        aroma_profiles: sensoryData.aroma_profile,
        flavor_intensity: sensoryData.flavor_intensity,
        flavor_quality: sensoryData.flavor_quality,
        flavor_profiles: sensoryData.flavor_profile,
        smoke_harshness: sensoryData.smoke_harshness,
        ash_color: sensoryData.ash_color,
        created_at: sensoryData.created_at,
        updated_at: sensoryData.updated_at
      } : undefined,
      tags,
      badges: selectionBadges,
      images: []  // These would need to be populated from somewhere else
    };
  } 
  // Implementation for the new overload (Selection with SensoryData)
  else {
    const sensoryData = dataOrSensory as SensoryData | null;
    const tags = (tagsOrSensory as SelectionTag[] || []);
    
    // Construct and return the enhanced selection
    const enhanced: EnhancedSelection = {
      id: selection.id,
      phenoId: selection.pheno_id,
      plantId: selection.plant_id,
      generation: selection.generation,
      selectionDate: selection.selection_date,
      selectionRationale: selection.selection_rationale,
      selectionNotes: selection.selection_notes,
      selectionHighlights: selection.selection_highlights || [],
      growthMetrics: selection.growth_metrics,
      sensoryData: sensoryData || undefined,
      tags: tags || [],
      badges: badges || [],
      images: []  // These would need to be populated from somewhere else
    };
    
    return enhanced;
  }
}

/**
 * Calculates percentile rank of a trait value within a dataset
 */
export function calculatePercentile(value: number, dataset: number[]): number {
  if (!dataset.length) return 0;
  
  const sortedData = [...dataset].sort((a, b) => a - b);
  const position = sortedData.findIndex(item => item >= value);
  
  if (position === -1) return 100; // Value is higher than all in dataset
  
  return Math.round((position / sortedData.length) * 100);
} 